/**
 * Intro Animation - Cinematic space-themed entry experience
 *
 * Features:
 * - Deep space background with stars, nebulae, and light glares
 * - Bio-inspired DNA helix animations
 * - Quantum/neural network particle systems
 * - Graceful transition from deep navy to aquamarine
 * - Lambda symbol emergence
 */

import { useState, useEffect, useCallback } from 'react';
import { cn } from '../../lib/utils';

interface IntroAnimationProps {
  onComplete: () => void;
  /** Duration of the intro in milliseconds */
  duration?: number;
}

export default function IntroAnimation({
  onComplete,
  duration = 6000,
}: IntroAnimationProps) {
  const [phase, setPhase] = useState<'space' | 'awakening' | 'transition' | 'complete'>('space');
  const [progress, setProgress] = useState(0);
  const [canSkip, setCanSkip] = useState(false);

  // Animation timeline
  useEffect(() => {
    const startTime = Date.now();

    // Enable skip after 1.5 seconds
    const skipTimer = setTimeout(() => setCanSkip(true), 1500);

    // Phase transitions
    const awakeningTimer = setTimeout(() => setPhase('awakening'), duration * 0.3);
    const transitionTimer = setTimeout(() => setPhase('transition'), duration * 0.6);
    const completeTimer = setTimeout(() => {
      setPhase('complete');
      setTimeout(onComplete, 500);
    }, duration);

    // Progress animation
    const progressInterval = setInterval(() => {
      const elapsed = Date.now() - startTime;
      setProgress(Math.min(elapsed / duration, 1));
    }, 50);

    return () => {
      clearTimeout(skipTimer);
      clearTimeout(awakeningTimer);
      clearTimeout(transitionTimer);
      clearTimeout(completeTimer);
      clearInterval(progressInterval);
    };
  }, [duration, onComplete]);

  const handleSkip = useCallback(() => {
    if (canSkip) {
      setPhase('complete');
      onComplete();
    }
  }, [canSkip, onComplete]);

  return (
    <div
      className={cn(
        'fixed inset-0 z-50 overflow-hidden cursor-pointer',
        'transition-opacity duration-1000',
        phase === 'complete' && 'opacity-0 pointer-events-none'
      )}
      onClick={handleSkip}
      role="button"
      tabIndex={0}
      onKeyDown={(e) => e.key === 'Enter' && handleSkip()}
      aria-label="Skip intro animation"
    >
      {/* Dynamic Background Gradient */}
      <div
        className="absolute inset-0 transition-all duration-[5000ms] ease-in-out"
        style={{
          background: phase === 'space'
            ? 'radial-gradient(ellipse at 30% 20%, #0a1628 0%, #050d1a 40%, #020810 100%)'
            : phase === 'awakening'
            ? 'radial-gradient(ellipse at 50% 50%, #0c1a2e 0%, #071320 50%, #040c18 100%)'
            : 'radial-gradient(ellipse at 50% 80%, #0f1e2d 0%, #0c1822 50%, #0f1419 100%)',
        }}
      />

      {/* Star Field - Multiple Layers for Depth */}
      <StarField layer="far" phase={phase} />
      <StarField layer="mid" phase={phase} />
      <StarField layer="near" phase={phase} />

      {/* Nebula Clouds */}
      <NebulaEffects phase={phase} />

      {/* Light Glares / Lens Flares */}
      <LightGlares phase={phase} />

      {/* DNA Helix Animation */}
      <DNAHelix phase={phase} />

      {/* Neural Network Particles */}
      <NeuralParticles phase={phase} />

      {/* Quantum Entanglement Lines */}
      <QuantumLines phase={phase} />

      {/* Central Lambda Emergence - starts invisible, grows gracefully */}
      <div
        className="absolute inset-0 flex items-center justify-center"
        style={{
          transition: 'all 5s ease-out',
        }}
      >
        <div
          style={{
            position: 'relative',
            opacity: phase === 'space' ? 0 : phase === 'awakening' ? 0.6 : 1,
            transform: phase === 'space' ? 'scale(0.6)' : phase === 'awakening' ? 'scale(0.85)' : 'scale(1)',
            filter: phase === 'space' ? 'blur(8px)' : 'blur(0px)',
            transition: 'all 5s ease-out',
          }}
        >
          {/* Lambda Glow */}
          <div
            style={{
              position: 'absolute',
              inset: 0,
              borderRadius: '50%',
              background: 'radial-gradient(circle, rgba(94, 234, 212, 0.5) 0%, transparent 70%)',
              filter: 'blur(50px)',
              transform: 'scale(4)',
            }}
          />

          {/* Lambda Symbol */}
          <span
            className="lambda-symbol"
            style={{
              fontSize: '180px',
              position: 'relative',
              zIndex: 10,
              color: phase === 'space' ? '#1e40af' : phase === 'awakening' ? '#38bdf8' : '#5eead4',
              textShadow: `
                0 0 40px rgba(94, 234, 212, 0.6),
                0 0 80px rgba(94, 234, 212, 0.4),
                0 0 120px rgba(94, 234, 212, 0.2)
              `,
              transition: 'color 5s ease-out',
            }}
          >
            Λ
          </span>
        </div>
      </div>

      {/* Brand Text Fade In */}
      <div
        className={cn(
          'absolute inset-x-0 bottom-[30%] flex flex-col items-center',
          'transition-all duration-[3500ms] ease-out',
          (phase === 'space' || phase === 'awakening') && 'opacity-0 translate-y-8',
          phase === 'transition' && 'opacity-100 translate-y-0',
          phase === 'complete' && 'opacity-100 translate-y-0'
        )}
      >
        <h1 className="lambda-brand text-3xl md:text-4xl tracking-[0.2em] mb-3">
          <span style={{ color: 'rgba(236, 254, 255, 0.9)' }}>LUKH</span>
          <span style={{ color: '#5eead4' }}>Λ</span>
          <span style={{ color: 'rgba(236, 254, 255, 0.9)' }}>S </span>
          <span style={{ color: '#5eead4' }}>Λ</span>
          <span style={{ color: 'rgba(236, 254, 255, 0.9)' }}>I</span>
        </h1>
        <p
          className="text-sm md:text-base tracking-widest uppercase"
          style={{ color: 'rgba(148, 163, 184, 0.8)' }}
        >
          Superior Consciousness Technology
        </p>
      </div>

      {/* Skip Indicator */}
      <div
        className={cn(
          'absolute bottom-8 left-1/2 -translate-x-1/2',
          'transition-opacity duration-500',
          canSkip ? 'opacity-60' : 'opacity-0'
        )}
      >
        <p
          className="text-xs tracking-wider"
          style={{ color: 'rgba(148, 163, 184, 0.5)' }}
        >
          Click anywhere to skip
        </p>
      </div>

      {/* Progress Bar */}
      <div className="absolute bottom-0 left-0 right-0 h-0.5 bg-black/20">
        <div
          className="h-full transition-all duration-100 ease-linear"
          style={{
            width: `${progress * 100}%`,
            background: 'linear-gradient(90deg, #1e40af 0%, #38bdf8 50%, #5eead4 100%)',
          }}
        />
      </div>
    </div>
  );
}

// ============================================================================
// Star Field Component - Static stars (no animation, like a photograph)
// ============================================================================

// Seeded random for consistent star positions
function seededRandom(seed: number) {
  const x = Math.sin(seed) * 10000;
  return x - Math.floor(x);
}

function StarField({ layer, phase }: { layer: 'far' | 'mid' | 'near'; phase: string }) {
  // Reduced star counts for better performance
  const config = {
    far: { count: 40, sizeRange: [0.5, 1.2], opacity: 0.5 },
    mid: { count: 25, sizeRange: [1, 2], opacity: 0.6 },
    near: { count: 15, sizeRange: [1.5, 2.5], opacity: 0.8 },
  }[layer];

  const layerSeed = layer === 'far' ? 1000 : layer === 'mid' ? 2000 : 3000;

  return (
    <div
      className={cn(
        'absolute inset-0 transition-opacity duration-[5000ms]',
        phase === 'transition' && 'opacity-50',
        phase === 'complete' && 'opacity-30'
      )}
    >
      {Array.from({ length: config.count }).map((_, i) => {
        // Use seeded random for consistent positions across renders
        const seed = layerSeed + i;
        const size = config.sizeRange[0] + seededRandom(seed) * (config.sizeRange[1] - config.sizeRange[0]);
        const x = seededRandom(seed + 100) * 100;
        const y = seededRandom(seed + 200) * 100;
        const colorSeed = seededRandom(seed + 300);

        // Static stars - no animation, just fixed opacity
        return (
          <div
            key={`${layer}-${i}`}
            className="absolute rounded-full"
            style={{
              width: `${size}px`,
              height: `${size}px`,
              left: `${x}%`,
              top: `${y}%`,
              backgroundColor: colorSeed < 0.2 ? '#7dd3fc' : colorSeed < 0.35 ? '#5eead4' : '#e0f2fe',
              opacity: config.opacity * (0.6 + seededRandom(seed + 400) * 0.4),
              boxShadow: size > 1.5 ? `0 0 ${size * 2}px currentColor` : 'none',
            }}
          />
        );
      })}
    </div>
  );
}

// ============================================================================
// Nebula Effects
// ============================================================================

function NebulaEffects({ phase }: { phase: string }) {
  return (
    <div
      className="absolute inset-0"
      style={{
        opacity: phase === 'space' ? 0.8 : phase === 'awakening' ? 0.6 : phase === 'transition' ? 0.4 : 0.2,
        transition: 'opacity 5s ease-out',
      }}
    >
      {/* Primary Nebula */}
      <div
        className="absolute w-[600px] h-[600px] rounded-full animate-float-slow"
        style={{
          left: '10%',
          top: '20%',
          background: 'radial-gradient(ellipse, rgba(30, 64, 175, 0.15) 0%, transparent 70%)',
          filter: 'blur(60px)',
        }}
      />

      {/* Secondary Nebula */}
      <div
        className="absolute w-[500px] h-[400px] rounded-full animate-float-slow"
        style={{
          right: '5%',
          bottom: '10%',
          background: 'radial-gradient(ellipse, rgba(94, 234, 212, 0.1) 0%, transparent 70%)',
          filter: 'blur(80px)',
          animationDelay: '-5s',
        }}
      />

      {/* Tertiary Nebula */}
      <div
        className="absolute w-[400px] h-[500px] rounded-full animate-float-slow"
        style={{
          left: '60%',
          top: '5%',
          background: 'radial-gradient(ellipse, rgba(125, 211, 252, 0.08) 0%, transparent 70%)',
          filter: 'blur(70px)',
          animationDelay: '-10s',
        }}
      />
    </div>
  );
}

// ============================================================================
// Light Glares / Lens Flares
// ============================================================================

function LightGlares({ phase }: { phase: string }) {
  return (
    <div
      className={cn(
        'absolute inset-0 pointer-events-none transition-opacity duration-[4000ms]',
        phase === 'space' && 'opacity-100',
        phase === 'awakening' && 'opacity-80',
        phase === 'transition' && 'opacity-40',
        phase === 'complete' && 'opacity-0'
      )}
    >
      {/* Main lens flare */}
      <div
        className="absolute w-[300px] h-[300px] animate-pulse"
        style={{
          left: '70%',
          top: '15%',
          background: 'radial-gradient(circle, rgba(255, 255, 255, 0.15) 0%, rgba(125, 211, 252, 0.05) 30%, transparent 70%)',
          filter: 'blur(2px)',
        }}
      />

      {/* Secondary flare */}
      <div
        className="absolute w-[150px] h-[150px]"
        style={{
          left: '20%',
          top: '60%',
          background: 'radial-gradient(circle, rgba(94, 234, 212, 0.1) 0%, transparent 70%)',
          filter: 'blur(4px)',
        }}
      />

      {/* Horizontal light streak */}
      <div
        className="absolute h-[1px] animate-shimmer"
        style={{
          left: '30%',
          right: '20%',
          top: '35%',
          background: 'linear-gradient(90deg, transparent, rgba(255, 255, 255, 0.1), transparent)',
        }}
      />
    </div>
  );
}

// ============================================================================
// DNA Helix Animation
// ============================================================================

function DNAHelix({ phase }: { phase: string }) {
  const helixPoints = 10;

  return (
    <div
      className="absolute inset-0 pointer-events-none"
      style={{
        opacity: phase === 'space' ? 0 : phase === 'awakening' ? 0.6 : phase === 'transition' ? 0.7 : 0,
        transition: 'opacity 5s ease-out',
      }}
    >
      <svg
        className="absolute left-[15%] top-1/2 -translate-y-1/2 w-[100px] h-[400px]"
        viewBox="0 0 100 400"
        style={{ transform: 'translateY(-50%) rotateY(20deg)' }}
      >
        {Array.from({ length: helixPoints }).map((_, i) => {
          const y = (i / helixPoints) * 400;
          const phase1 = (i * 0.5) + (Date.now() / 1000);
          const x1 = 50 + Math.sin(phase1) * 30;
          const x2 = 50 - Math.sin(phase1) * 30;

          return (
            <g key={i} className="animate-dna-spin" style={{ animationDelay: `${i * 0.1}s` }}>
              {/* Backbone strand 1 */}
              <circle
                cx={x1}
                cy={y}
                r="4"
                fill="#38bdf8"
                opacity={0.6}
              />
              {/* Backbone strand 2 */}
              <circle
                cx={x2}
                cy={y}
                r="4"
                fill="#5eead4"
                opacity={0.6}
              />
              {/* Base pair connection */}
              <line
                x1={x1}
                y1={y}
                x2={x2}
                y2={y}
                stroke="rgba(148, 163, 184, 0.3)"
                strokeWidth="1"
              />
            </g>
          );
        })}
      </svg>

      {/* Second helix on right */}
      <svg
        className="absolute right-[15%] top-1/2 -translate-y-1/2 w-[80px] h-[300px]"
        viewBox="0 0 80 300"
        style={{ transform: 'translateY(-50%) rotateY(-20deg)' }}
      >
        {Array.from({ length: 9 }).map((_, i) => {
          const y = (i / 9) * 300;
          const phaseOffset = (i * 0.5) + 2;
          const x1 = 40 + Math.sin(phaseOffset) * 25;
          const x2 = 40 - Math.sin(phaseOffset) * 25;

          return (
            <g key={i} style={{ animationDelay: `${i * 0.15}s` }}>
              <circle cx={x1} cy={y} r="3" fill="#7dd3fc" opacity={0.5} />
              <circle cx={x2} cy={y} r="3" fill="#2dd4bf" opacity={0.5} />
              <line x1={x1} y1={y} x2={x2} y2={y} stroke="rgba(148, 163, 184, 0.2)" strokeWidth="1" />
            </g>
          );
        })}
      </svg>
    </div>
  );
}

// ============================================================================
// Neural Network Particles
// ============================================================================

function NeuralParticles({ phase }: { phase: string }) {
  // Fewer but larger, more visible particles
  const nodes = 12;

  return (
    <div
      className="absolute inset-0 pointer-events-none"
      style={{
        opacity: phase === 'space' ? 0 : phase === 'awakening' ? 0.5 : phase === 'transition' ? 0.8 : 0,
        transition: 'opacity 5s ease-out',
      }}
    >
      {Array.from({ length: nodes }).map((_, i) => {
        // Use seeded random for consistent positions
        const seed = 5000 + i;
        const x = 15 + seededRandom(seed) * 70;
        const y = 15 + seededRandom(seed + 100) * 70;
        // Much larger particles (8-16px)
        const size = 8 + seededRandom(seed + 200) * 8;
        const colorSeed = seededRandom(seed + 300);

        return (
          <div
            key={i}
            className="absolute rounded-full"
            style={{
              left: `${x}%`,
              top: `${y}%`,
              width: `${size}px`,
              height: `${size}px`,
              backgroundColor: colorSeed < 0.33 ? '#5eead4' : colorSeed < 0.66 ? '#7dd3fc' : '#a5b4fc',
              boxShadow: `0 0 ${size * 4}px ${colorSeed < 0.33 ? '#5eead4' : colorSeed < 0.66 ? '#7dd3fc' : '#a5b4fc'}`,
              opacity: 0.8,
            }}
          />
        );
      })}

      {/* Connection lines - brighter */}
      <svg className="absolute inset-0 w-full h-full">
        {Array.from({ length: 10 }).map((_, i) => {
          const seed = 6000 + i;
          const x1 = 20 + seededRandom(seed) * 60;
          const y1 = 20 + seededRandom(seed + 100) * 60;
          const x2 = 20 + seededRandom(seed + 200) * 60;
          const y2 = 20 + seededRandom(seed + 300) * 60;

          return (
            <line
              key={i}
              x1={`${x1}%`}
              y1={`${y1}%`}
              x2={`${x2}%`}
              y2={`${y2}%`}
              stroke="#5eead4"
              strokeWidth="1"
              opacity={0.5}
            />
          );
        })}
      </svg>
    </div>
  );
}

// ============================================================================
// Quantum Entanglement Lines
// ============================================================================

function QuantumLines({ phase }: { phase: string }) {
  return (
    <div
      className="absolute inset-0 pointer-events-none"
      style={{
        opacity: phase === 'space' ? 0 : phase === 'awakening' ? 0.4 : phase === 'transition' ? 0.8 : 0,
        transition: 'opacity 5s ease-out',
      }}
    >
      <svg className="absolute inset-0 w-full h-full">
        {/* Orbital rings around center - brighter, more visible */}
        <ellipse
          cx="50%"
          cy="50%"
          rx="18%"
          ry="7%"
          fill="none"
          stroke="#5eead4"
          strokeWidth="1.5"
          opacity={0.7}
          className="animate-orbit"
          style={{ transformOrigin: '50% 50%', filter: 'drop-shadow(0 0 4px #5eead4)' }}
        />
        <ellipse
          cx="50%"
          cy="50%"
          rx="22%"
          ry="9%"
          fill="none"
          stroke="#7dd3fc"
          strokeWidth="1.5"
          opacity={0.6}
          className="animate-orbit-reverse"
          style={{ transformOrigin: '50% 50%', filter: 'drop-shadow(0 0 4px #7dd3fc)' }}
        />
        <ellipse
          cx="50%"
          cy="50%"
          rx="14%"
          ry="14%"
          fill="none"
          stroke="#38bdf8"
          strokeWidth="1"
          opacity={0.5}
          className="animate-orbit"
          style={{ transformOrigin: '50% 50%', animationDuration: '45s', filter: 'drop-shadow(0 0 3px #38bdf8)' }}
        />
      </svg>
    </div>
  );
}
